import React, { useState, useRef, useEffect } from "react";

const DateRangePicker = ({ onChange, dateRange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Derive selectedOption from dateRange, default to "thisweek" if not provided
  const selectedOption = dateRange?.range || "thisweek";

  const handleOptionClick = (option) => {
    setIsOpen(false);
    const today = new Date();
    let startDate, endDate;

    switch (option) {
      case "last7days":
        startDate = new Date(today);
        startDate.setDate(today.getDate() - 6);
        endDate = today;
        break;
      case "lastweek":
        startDate = new Date(today);
        const dayOfWeek = startDate.getDay();
        const diff = startDate.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1) - 7;
        startDate.setDate(diff);
        startDate.setHours(0, 0, 0, 0);
        endDate = new Date(startDate);
        endDate.setDate(startDate.getDate() + 6);
        endDate.setHours(23, 59, 59, 999);
        break;
      case "thisweek":
        startDate = new Date(today);
        const currDayOfWeek = startDate.getDay();
        const diffToMonday = startDate.getDate() - currDayOfWeek + (currDayOfWeek === 0 ? -6 : 1);
        startDate.setDate(diffToMonday);
        startDate.setHours(0, 0, 0, 0);
        endDate = today;
        break;
      case "last30days":
        startDate = new Date(today);
        startDate.setDate(today.getDate() - 29);
        endDate = today;
        break;
      case "thismonth":
        startDate = new Date(today.getFullYear(), today.getMonth(), 1);
        endDate = today;
        break;
      case "lastmonth":
        // First day of the previous month
        startDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        startDate.setHours(0, 0, 0, 0);
        
        // Last day of the previous month (day 0 of current month is last day of previous month)
        endDate = new Date(today.getFullYear(), today.getMonth(), 0);
        endDate.setHours(23, 59, 59, 999);
        break;
      case "last3months":
        startDate = new Date(today);
        startDate.setMonth(today.getMonth() - 3);
        startDate.setDate(1);
        startDate.setHours(0, 0, 0, 0);
        endDate = today;
        break;
      case "last6months":
        startDate = new Date(today);
        startDate.setMonth(today.getMonth() - 6);
        startDate.setDate(1);
        startDate.setHours(0, 0, 0, 0);
        endDate = today;
        break;
      default:
        startDate = new Date(today);
        const defaultDayOfWeek = startDate.getDay();
        const defaultDiffToMonday = startDate.getDate() - defaultDayOfWeek + (defaultDayOfWeek === 0 ? -6 : 1);
        startDate.setDate(defaultDiffToMonday);
        startDate.setHours(0, 0, 0, 0);
        endDate = today;
    }

    if (onChange) {
      onChange({ startDate, endDate, range: option });
    }
  };

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const getDisplayText = (option = selectedOption) => {
    switch (option) {
      case "thisweek": return "This week";
      case "lastweek": return "Last week";
      case "last7days": return "Last 7 days";
      case "last30days": return "Last 30 days";
      case "thismonth": return "This month";
      case "lastmonth": return "Last month";
      case "last3months": return "Last 3 months";
      case "last6months": return "Last 6 months";
      default: return "This week";
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="relative inline-block" ref={dropdownRef}>
      <button
        onClick={toggleDropdown}
        className="inline-flex items-center justify-between bg-[#EDEDFD] border border-[#DADAFB] rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-50"
      >
        <div className="flex items-center gap-2">
        <svg  xmlns="http://www.w3.org/2000/svg"  width="20"  height="20"  viewBox="0 0 24 24"  fill="none"  stroke="currentColor"  strokeWidth="1.5"  strokeLinecap="round"  strokeLinejoin="round"  className="icon icon-tabler icons-tabler-outline icon-tabler-calendar-week text-black-0"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M4 7a2 2 0 0 1 2 -2h12a2 2 0 0 1 2 2v12a2 2 0 0 1 -2 2h-12a2 2 0 0 1 -2 -2v-12z" /><path d="M16 3v4" /><path d="M8 3v4" /><path d="M4 11h16" /><path d="M7 14h.013" /><path d="M10.01 14h.005" /><path d="M13.01 14h.005" /><path d="M16.015 14h.005" /><path d="M13.015 17h.005" /><path d="M7.01 17h.005" /><path d="M10.01 17h.005" /></svg>
          <span className="text-black-0 text-sm">{getDisplayText()}</span>
        </div>
        <svg 
          className="w-4 h-4 ml-2" 
          xmlns="http://www.w3.org/2000/svg" 
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 011.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
        </svg>
      </button>
      
      {isOpen && (
        <div className="absolute z-[9999] mt-1 w-[200px] bg-white-f border border-grey-20 rounded-md shadow-subtle">
          <div className="max-h-60 overflow-y-auto">
            {["thisweek", "lastweek", "last7days", "last30days", "thismonth", "lastmonth", "last3months", "last6months"].map((option) => (
              <div
                key={option}
                className={`px-4 py-2 hover:bg-grey-10 cursor-pointer ${selectedOption === option ? "bg-grey-10" : ""}`}
                onClick={() => handleOptionClick(option)}
              >
                <span className="text-sm text-black-0">{getDisplayText(option)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default DateRangePicker; 